import { postUsers } from '@/api/oversight';
import { OutlinedButton, StandardButton } from '@/components/UI/Button';
import {
  menuItemDefaultStyle,
  StyledTextField,
  WhiteCircularProgress,
} from '@/components/UI/Typography';
import { IconAddMS, IconCloseMS, IconPersonAddMS } from '@/constants/icons';
import { Constants } from '@/constants/oversight';
import { validateEmail } from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { Button, MenuItem } from '@mui/material';
import { useState } from 'react';

import {
  ActionsBox,
  CloseButton,
  DialogContainer,
  FormStyled,
  StyledDialog,
  StyledTitle,
  TopBox,
} from './styles';

const roleOptions = [
  { value: 'select', label: Constants.new_user_role_placeholder },
  { value: 'operator', label: Constants.new_user_role_operator },
  { value: 'auditor', label: Constants.new_user_role_auditor },
  { value: 'supervisor', label: Constants.new_user_role_supervisor },
  { value: 'admin', label: Constants.new_user_role_admin },
];

interface NewAdministrativeUserProps {
  onUserCreated: () => void;
}

export default function NewAdministrativeUser({
  onUserCreated,
}: NewAdministrativeUserProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('select');
  const [loading, setLoading] = useState(false);

  const { oversightUserRole } = useTokenRefresh();
  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  // Only an admin may create another admin.
  const visibleRoleOptions = roleOptions.filter(
    (option) => option.value !== 'admin' || oversightUserRole === 'admin'
  );

  const canSave =
    name.trim() !== '' && validateEmail(email) && role !== 'select';

  const handleOpen = (): void => setOpen(true);

  const handleClose = (): void => {
    setOpen(false);
    setName('');
    setEmail('');
    setRole('select');
  };

  const handleSave = async (): Promise<void> => {
    setLoading(true);

    try {
      const { data, detail } = await postUsers({
        name: name.trim(),
        email,
        role,
      });

      if (detail?.description) {
        throw new Error(detail.description);
      }

      if (!data) throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar(Constants.new_user_success, false);
      handleClose();
      onUserCreated();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <StandardButton onClick={handleOpen}>
        {IconAddMS}
        {Constants.new_user_add}
      </StandardButton>
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="add new user"
      >
        <DialogContainer>
          <TopBox>
            <CloseButton onClick={handleClose}>{IconCloseMS}</CloseButton>
          </TopBox>
          <StyledTitle>
            {IconPersonAddMS}
            {Constants.new_user_add}
          </StyledTitle>
          <FormStyled>
            <StyledTextField
              required
              id="name"
              value={name}
              label={Constants.new_user_name}
              placeholder={Constants.new_user_name_placeholder}
              onChange={(e) => setName(e.target.value)}
            />
            <StyledTextField
              required
              id="email"
              type="email"
              value={email}
              label={Constants.new_user_email}
              placeholder={Constants.new_user_email_placeholder}
              onChange={(e) => setEmail(e.target.value)}
            />
            <StyledTextField
              id="role"
              select
              required
              value={role}
              label={Constants.new_user_role}
              onChange={(e) => setRole(e.target.value)}
            >
              {visibleRoleOptions.map((option) => (
                <MenuItem
                  disabled={option.value === 'select'}
                  key={option.value}
                  value={option.value}
                  sx={{ ...menuItemDefaultStyle }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </StyledTextField>
            <ActionsBox>
              <OutlinedButton onClick={handleClose}>
                {Constants.new_user_cancel}
              </OutlinedButton>
              <Button disabled={!canSave || loading} onClick={handleSave}>
                {loading ? (
                  <WhiteCircularProgress size={22} />
                ) : (
                  Constants.new_user_save
                )}
              </Button>
            </ActionsBox>
          </FormStyled>
        </DialogContainer>
      </StyledDialog>
    </>
  );
}
